import { Component, Inject } from "@angular/core";
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Observable } from "rxjs";
import { AdmTable } from "../../api/models/adm-table";
import { AdmTableColumn } from "../../api/models/adm-table-column";
import { AdmFieldGroup } from "../../api/models/adm-field-group";
import { AdmRelationship } from "../../api/models/adm-relationship";
import { AdmSaveFlattenedColumnRequest } from "../../api/models/adm-save-flattened-column-request";
import { DialogResult, DialogResultType } from "../DialogResult";

export interface AdmFlattenedColumnDialogData {
  flattened: AdmSaveFlattenedColumnRequest;
  relationshipPath: number[];
  tables: AdmTable[];
  columns: AdmTableColumn[];
  fieldGroups: AdmFieldGroup[];
  relationships: AdmRelationship[];
  // AdmSaveFlattenedColumnRequest has no Retired field (SaveFlattenedColumn never touches it), so the original
  // row's status travels separately - this is what makes the Retire/Restore button possible.
  retired: boolean;
}

// Admin ADM Definition > Flattened Columns > one virtual column resolved through a chain of relationships. Builds
// the path one hop at a time (each hop must start at the table the previous one ended on), so this dialog owns the
// path-building state and helper lookups (moved here from admin-adm-definition.component.ts's own flattenPathIds
// and nextPathRelationships/pathEndTableId/pathLabel) rather than the host page.
@Component({
  selector: "adm-flattened-column-dialog",
  templateUrl: "./adm-flattened-column-dialog.component.html",
})
export class AdmFlattenedColumnDialogComponent {
  public result: AdmSaveFlattenedColumnRequest;
  public pathIds: number[];
  public Result: DialogResult = { ResultType: DialogResultType.Cancel, ResultData: null };

  // The names last filled in automatically from the source column, so a later change of source column can
  // replace them but never a name the admin typed (or edited) by hand.
  private autoOutputName = "";
  private autoDisplayName = "";

  constructor(public dialogRef: MatDialogRef<AdmFlattenedColumnDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: AdmFlattenedColumnDialogData) {
    this.result = { ...data.flattened };
    this.pathIds = [...data.relationshipPath];
  }

  get isNew(): boolean {
    return !this.data.flattened.admTableColumnId;
  }

  tableName(id: number): string {
    const t = this.data.tables.find((x) => x.admTableId === id);
    return t ? t.tableName : `#${id}`;
  }

  columnLabel(id: number): string {
    const c = this.data.columns.find((x) => x.admTableColumnId === id);
    return c ? `${this.tableName(c.admTableId)}.${c.outputName || c.columnName}` : `#${id}`;
  }

  relationshipLabel(r: AdmRelationship): string {
    return `${this.columnLabel(r.fromColumnId)} → ${this.columnLabel(r.toColumnId)}`;
  }

  fieldGroupsOf(tableId: number | null): AdmFieldGroup[] {
    return this.data.fieldGroups.filter((g) => g.admTableId === tableId).sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  }

  columnsOf(tableId: number | null): AdmTableColumn[] {
    return this.data.columns.filter((c) => c.admTableId === tableId);
  }

  pathLabel(path: string | undefined): string {
    if (!path) return "";
    return path.split(",").map((id) => {
      const rel = this.data.relationships.find((r) => r.admRelationshipId === Number(id));
      return rel ? this.relationshipLabel(rel) : `#${id}`;
    }).join("  →  ");
  }

  // Relationships that can extend the path from the current end: the first must start at the parent table, each
  // next one at the table the previous ended on.
  get nextPathRelationships(): AdmRelationship[] {
    let currentTable = this.result.parentTableId;
    for (const id of this.pathIds) {
      const rel = this.data.relationships.find((r) => r.admRelationshipId === id);
      if (!rel) return [];
      const to = this.data.columns.find((c) => c.admTableColumnId === rel.toColumnId);
      if (!to) return [];
      currentTable = to.admTableId;
    }
    return this.data.relationships.filter((r) => {
      const from = this.data.columns.find((c) => c.admTableColumnId === r.fromColumnId);
      return !r.retired && !!from && from.admTableId === currentTable && r.cardinality !== 2;
    });
  }

  // The table the chain currently ends on - its physical columns are the candidate source columns.
  get pathEndTableId(): number | null {
    if (this.pathIds.length === 0) return null;
    const last = this.data.relationships.find((r) => r.admRelationshipId === this.pathIds[this.pathIds.length - 1]);
    const to = last ? this.data.columns.find((c) => c.admTableColumnId === last.toColumnId) : null;
    return to ? to.admTableId : null;
  }

  // Picking the source column proposes its name for the output name and its display name for the display name
  // (falling back to its name), each only while the field is empty or still holds the previous proposal.
  onSourceColumnChange(sourceColumnId: number): void {
    const source = this.data.columns.find((c) => c.admTableColumnId === sourceColumnId);
    if (!source) return;
    const output = source.outputName || source.columnName;
    const display = source.displayName || output;
    if (!this.result.outputName || this.result.outputName === this.autoOutputName) this.result.outputName = output;
    if (!this.result.displayName || this.result.displayName === this.autoDisplayName) this.result.displayName = display;
    this.autoOutputName = output;
    this.autoDisplayName = display;
  }

  addPathStep(relationshipId: number) {
    this.pathIds = [...this.pathIds, relationshipId];
    this.result.sourceColumnId = 0;
  }

  removeLastPathStep() {
    this.pathIds = this.pathIds.slice(0, -1);
    this.result.sourceColumnId = 0;
  }

  onSave(): void {
    this.Result.ResultType = DialogResultType.Ok;
    this.Result.ResultData = { ...this.result, relationshipPath: this.pathIds };
    this.dialogRef.close(this.Result);
  }

  // ResultData carries the CURRENT (untouched) retired state, same convention every other ADM dialog's
  // Retire button uses - the host is what flips it, via setRetired's own `!retired` (see admin-adm-definition
  // .component.ts's openXDialog methods).
  onToggleRetired(): void {
    this.Result.ResultType = DialogResultType.Delete;
    this.Result.ResultData = { admTableColumnId: this.result.admTableColumnId, retired: this.data.retired };
    this.dialogRef.close(this.Result);
  }

  // Yes = "permanently delete, confirmed" here, distinct from Delete (Retire/Restore) above - this dialog's own
  // contract with admin-adm-definition.component.ts's openFlattenedDialog, not used anywhere else.
  onDelete(): void {
    if (!confirm(`Delete the flattened column "${this.result.outputName}"? This cannot be undone.`)) return;
    this.Result.ResultType = DialogResultType.Yes;
    this.Result.ResultData = { admTableColumnId: this.result.admTableColumnId };
    this.dialogRef.close(this.Result);
  }

  onCancelClick(): void {
    this.dialogRef.close(this.Result);
  }

  static showDialog(dialog: MatDialog, data: AdmFlattenedColumnDialogData): Observable<DialogResult> {
    return dialog.open(AdmFlattenedColumnDialogComponent, { disableClose: false, width: "800px", data }).afterClosed();
  }
}
